import Datadog from '../_shared/datadog';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ENV, NODE_ENV } from '@env';
import { IRootStore } from 'src/store/store';
import makeInspectable from 'mobx-devtools-mst';
import { useBrowserPinchToZoom } from 'src/hooks/useBrowserPinchToZoom';
import { DATADOG_CUSTOM_ACTIONS } from '../_shared/datadog/DatadogCustomActions';
import { BOOTSTRAP_CONTEXT, captureConnectionContext } from '../_shared/datadog/bootstrapContext';
import Sentry from 'src/_shared/sentry/Sentry';
import { initialStore, RootStoreContext } from 'src/contexts/StoreContext';
import { APP_VERSION, SDK_VERSION } from '../app/settings';
import { initStore } from 'src/store/store.utils';
import { reportBootstrapFailure } from 'src/_shared/datadog/bootstrap-steps/bootstrapSteps';
import { logInfo, logWarning } from 'src/_shared/logger';
import Didomi from '@photosi/rn-components/didomi';

import { configureReanimatedLogger, ReanimatedLogLevel } from 'react-native-reanimated';
import isDev from 'src/_shared/utils/isDev';
import { isSupportedEnv } from 'src/_shared/utils/is-supported-env/isSupportedEnv';
import { UnsupportedEnvScreen } from '@photosi/rn-components/unsupport-env-screen/UnsupportedEnvScreen';
import { PageLoader } from '@photosi/rn-components/page-loader/PageLoader';
import { checkJwtPreInit } from '../_shared/utils/auth/checkJwtPreInit';
import { GlobalErrorBoundary } from '@photosi/rn-components/global-error-boundary/GlobalErrorBoundary';
import { FontsProvider } from '@photosi/rn-components/fonts-provider/FontsProvider';
import { useFontsContext } from 'src/contexts/FontsContext';
import { IsSupportedEnvResult } from 'src/_shared/utils/is-supported-env/isSupportedEnv.types';
import { FocusProvider } from '@photosi/rn-components/focus-provider/FocusProvider';

export function withInitEnv(App: React.ComponentType) {
  console.log(
    'APP_VERSION:',
    APP_VERSION,
    '|| SENTRY_DIST:',
    process.env.SENTRY_DIST,
    '|| SDK_VERSION:',
    SDK_VERSION,
    '|| ENV:',
    ENV,
    '|| NODE_ENV:',
    NODE_ENV,
    '|| LAST_BUILD_DATE:',
    process.env.LAST_BUILD_DATE ?? 'unknown',
  );

  /* TECHNICAL SDKs don't require user consents to be initialized */
  Datadog.init();
  Sentry.init(App);

  return () => {
    // set the initial store to handle i18n in all components, ready flag is for loading screen
    const [store, setStore] = useState(initialStore);

    // isAppReady flag is used to not show the main app until store, fonts and didomi are ready
    const [isAppReady, setIsAppReady] = useState(false);

    // canInitConsent flag is used to load Didomi only once the user can stay on the page (no redirect to login)
    const [canInitConsent, setCanInitConsent] = useState(false);

    // isSupported flag is used to show the UnsupportedEnvScreen if the env is not supported
    const [{ isSupported, reason }, setIsSupported] = useState<IsSupportedEnvResult>({
      isSupported: undefined,
      reason: undefined,
    });

    useBrowserPinchToZoom();

    configureReanimatedLogger({
      level: isDev() ? ReanimatedLogLevel.warn : ReanimatedLogLevel.error,
      strict: true,
    });

    const onAppReady = useCallback(() => {
      logInfo('[withInitEnv] App is Ready!!!');
      setIsAppReady(true);
    }, []);

    const onStoreReady = useCallback((newStore: IRootStore) => {
      logInfo('[withInitEnv] Store is ready!!!');
      setStore(newStore);
    }, []);

    const onJwtChecked = useCallback(() => {
      setCanInitConsent(true);
    }, []);

    return (
      <>
        <BootstrapChecker setIsSupported={setIsSupported} onJwtChecked={onJwtChecked} />
        <RootStoreContext.Provider value={store}>
          <FocusProvider>
            <GlobalErrorBoundary>
              <FontsProvider>
                {typeof isSupported === 'boolean' && isSupported ? (
                  <AppLoader
                    App={App}
                    isAppReady={isAppReady}
                    onAppReady={onAppReady}
                    onStoreReady={onStoreReady}
                    isStoreReady={store.isStoreReady}
                    canInitConsent={canInitConsent}
                  />
                ) : typeof isSupported === 'boolean' && !isSupported ? (
                  <UnsupportedEnvScreen reason={reason} />
                ) : (
                  <></>
                )}
                {typeof isSupported === 'undefined' || !isAppReady ? <PageLoader showSentence={true} /> : <></>}
              </FontsProvider>
            </GlobalErrorBoundary>
          </FocusProvider>
        </RootStoreContext.Provider>
      </>
    );
  };
}

const AppLoader = ({
  App,
  isAppReady,
  onAppReady,
  isStoreReady,
  onStoreReady,
  canInitConsent,
}: {
  App: React.ComponentType;
  isAppReady: boolean;
  isStoreReady: boolean;
  onAppReady: () => void;
  onStoreReady: (store: IRootStore) => void;
  canInitConsent: boolean;
}) => {
  const [isDidomiReady, setIsDidomiReady] = useState<boolean>(false);
  const { areMainFontsLoaded } = useFontsContext();

  useEffect(() => {
    initStore()
      .then(store => {
        makeInspectable(store);
        onStoreReady(store);
        Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_store_ready, {
          ...BOOTSTRAP_CONTEXT,
          ...captureConnectionContext(),
        });
        logInfo('[withInitEnv] COMPLETED: store initialized in SDK', store);
      })
      .catch(error => {
        Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_store_ready);
        reportBootstrapFailure(error);
        logWarning('[withInitEnv] FAILED: store initialized in SDK', error);
      });
  }, []);

  const handleDidomiReady = useCallback(() => {
    setIsDidomiReady(true);
    Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_app_gate_ready, {
      ...BOOTSTRAP_CONTEXT,
      ...captureConnectionContext(),
    });
  }, []);

  useEffect(() => {
    if (isStoreReady && areMainFontsLoaded && isDidomiReady) {
      onAppReady();
    }
  }, [isStoreReady, areMainFontsLoaded, isDidomiReady]);

  return (
    <>
      {canInitConsent ? <Didomi handleDidomiReady={handleDidomiReady} /> : <></>}
      {isAppReady ? <App /> : <></>}
    </>
  );
};
const BootstrapChecker = ({
  setIsSupported,
  onJwtChecked,
}: {
  setIsSupported: React.Dispatch<React.SetStateAction<IsSupportedEnvResult>>;
  onJwtChecked: () => void;
}) => {
  const hasAlreadyInitialized = useRef(false);

  useEffect(() => {
    if (!hasAlreadyInitialized.current) {
      logInfo('[withInitEnv] Starting bootstrap checks...');

      hasAlreadyInitialized.current = true;

      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.editor_ready_time);
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_fonts_ready);
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_store_ready);
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_didomi_consent_ready);
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_app_gate_ready);

      let isRedirectingToLogin = false;

      isSupportedEnv()
        .then((isSupportedEnvResult: IsSupportedEnvResult) => {
          setIsSupported(isSupportedEnvResult);
          if (!isSupportedEnvResult.isSupported) {
            throw new Error('isSupportedEnv: ' + isSupportedEnvResult.reason);
          } else {
            return checkJwtPreInit().then(({ shouldContinueInit }) => {
              if (!shouldContinueInit) {
                isRedirectingToLogin = true;
                throw new Error('checkJwtPreInit: ' + shouldContinueInit);
              }
              onJwtChecked();
              return shouldContinueInit;
            });
          }
        })
        .then(results => {
          logInfo('[withInitEnv] COMPLETED Bootstrap check: ', results);
        })
        .catch(error => {
          logInfo('[withInitEnv] COMPLETED Bootstrap check with error: ', error);

          // only a redirect to login skips Didomi: on other failures it still loads, or the app would never be ready
          if (!isRedirectingToLogin) {
            onJwtChecked();
          }

          Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS.editor_ready_time);
          Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_fonts_ready);
          Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_store_ready);
          Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_didomi_consent_ready);
          Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS.bootstrap_app_gate_ready);
        });
    }
  }, []);

  return <></>;
};
